import { useCallback, useMemo } from 'react'
import _ from 'lodash'
import { useFormDispatch, useFormSelector } from '../../../redux/formStoreHooks'
import L3DiagnosisReview from '../l3/L3DiagnosisReview'
import { CodeNext, RenderMedia } from '@fusion-advantage/backcompat'
import { AssertionStatus, DiagnosisReviewItem } from '@fusion-advantage/formlib'
import { commitDiagnosisReviewComment, editDiagnosisReviewDisplayingComment, selectDiagnosisReviewDisplayingComments, selectDiagnosisReviewEditingComment, selectDiagnosisReviewIsWritable, selectDiagnosisReviewSortedModels, setDiagnosisReviewDisplayingComments, setDiagnosisReviewEditingComments, updateDiagnosisItemByIdx } from '../../../redux/diagnosisReviewSlice'
import { L3EditCommentDialog, L3ReadCommentDialog } from '../l3/L3CommentDialog'
import { selectExport } from '../../../redux/selectForm'

export const L4DiagnosisClinicianReview = () => {
	const writable = useFormSelector(selectDiagnosisReviewIsWritable)
	const media = useFormSelector(state => state.render.media)
	const codeNext = useFormSelector(state => selectExport(state)?.Form.Encounter.CodeNext)
	const sortedModels = useFormSelector(selectDiagnosisReviewSortedModels)
	const displayingComments = useFormSelector(selectDiagnosisReviewDisplayingComments)
	const editingCommentRef = useFormSelector(state => state.diagnosisReview.editingComment)
	const editingComment = useFormSelector(selectDiagnosisReviewEditingComment)
	const encounterAppLastStatusDT = useFormSelector(state => selectExport(state)?.Form.Encounter.EncounterAppLastStatusDT)
	const dispatch = useFormDispatch()

	const handleDiagnosisChange = useCallback((modelIdx: number, updatedModel: DiagnosisReviewItem) => {
		if(!writable) return
		dispatch(updateDiagnosisItemByIdx({
			idx: modelIdx,
			item: updatedModel,
		}))
	}, [writable, dispatch])

	const handleAuditCommentClick = useCallback((modelIdx: number) => {
		dispatch(setDiagnosisReviewDisplayingComments({
			itemIdx: modelIdx,
			commentType: 'auditComments',
		}))
	}, [dispatch])

	const handleAuditCommentSave = useCallback((message: string) => {
		// const itemIdx = editingCommentRef!.itemIdx
		dispatch(commitDiagnosisReviewComment(message))
		dispatch(setDiagnosisReviewEditingComments(null))
		// [ISSUE-485] Close comment box when confirm comment button is clicked
		// dispatch(setDiagnosisReviewDisplayingComments({
		// 	itemIdx,
		// 	commentType: 'auditComments'
		// }))
	}, [dispatch])

	const displayModels = useMemo(() => {
		let models = sortedModels
		if(media === RenderMedia.Print) {
			models = sortedModels.filter(({model}) => (
				model.status === AssertionStatus.Asserted ||
				model.status === AssertionStatus.HistoryOf
			))
		}
		return models
	}, [sortedModels, media])

	return (
		<>
			<L3DiagnosisReview
				isQueryReview={media !== RenderMedia.Print && codeNext === CodeNext.ClinicianAuditReview}
				models={displayModels}
				onUpdateDiagnosis={handleDiagnosisChange}
				onAuditCommentClick={handleAuditCommentClick}
			/>
			{displayingComments ? (
				<L3ReadCommentDialog
					title="Comments"
					show={true}
					comments={displayingComments}
					encounterAppLastStatusDT={encounterAppLastStatusDT}
					onAddClick={writable ? () => dispatch(editDiagnosisReviewDisplayingComment('new')) : undefined}
					onEditClick={writable ? (commentIdx: number) => dispatch(editDiagnosisReviewDisplayingComment(commentIdx)) : undefined}
					onClose={() => dispatch(setDiagnosisReviewDisplayingComments(null))}
				/>
			) : null}
			{editingCommentRef ? (
				<L3EditCommentDialog
					title={editingCommentRef.commentIdx === 'new' ? 'Add Comment' : 'Edit Comment'}
					show={true}
					existingMessage={editingComment?.message ?? editingCommentRef.defaultContent}
					onSave={handleAuditCommentSave}
					onClose={() => dispatch(setDiagnosisReviewEditingComments(null))}
				/>
			) : null}
		</>
	)
}
